import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { useCallback } from 'react';
import { FormikValues } from 'formik';
import {
  Appointment,
  AppointmentWithCustomerAndEmployee,
} from '../../interfaces';
import useGetDepositTransferAppointmets from '../../hooks/appointments/useGetDepositTransferAppointmets';
import FormSmallModal, { FormData } from '../global/FormSmallModal';
import {
  appointmentTransferDepositSchema,
  AppointmentTransferDepositState,
} from '../../types/appointments';
import { getFullName } from '../../helpers/global';
import { formatDollarAmount } from '../../utils/global/helper';
import useTransferAppointmentDeposit from '../../hooks/appointments/useTransferAppointmentDeposit';
import {
  invalidateAppointmentPayments,
  invalidateDepositTransferAppointments,
} from '../../services/appointments';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  appointment: Appointment;
  onRefresh: () => void | Promise<void>;
}

const getAppointmentLabel = (
  appointment: AppointmentWithCustomerAndEmployee,
  omitDeposit = false,
) =>
  `${getFullName(appointment.customer)} • ${format(
    new Date(appointment.startDateTime!),
    // Jan. 10, 2024 12:00 PM
    'MMM d, yyyy h:mm a',
  )}${
    omitDeposit
      ? ''
      : ` • ${formatDollarAmount(appointment.depositPrice || 0, true)}`
  }`;

export default function TransferAppointmentDepositModal({
  isOpen,
  onClose,
  appointment,
  onRefresh,
}: Props) {
  const { data, isLoading, isError } = useGetDepositTransferAppointmets({
    customerId: appointment.customerId,
    enabled: isOpen,
  });

  const refresh = useCallback(async () => {
    await onRefresh();
    await invalidateAppointmentPayments(appointment.appointmentId);
  }, [onRefresh, appointment.appointmentId]);

  const { mutateAsync } = useTransferAppointmentDeposit({
    onRefresh: refresh,
    onSuccess: () => {
      onClose();
      invalidateDepositTransferAppointments();
    },
    onError: toast.error,
  });

  const form: FormData<AppointmentTransferDepositState> = {
    title: 'Transfer a Deposit',
    initialValues: {
      fromAppointmentId: '',
      toAppointmentId: appointment.appointmentId,
    },
    validationSchema: appointmentTransferDepositSchema,
    onSubmit: async values => {
      await mutateAsync({ ...values, customerId: appointment.customerId });
    },
    inputRows: [
      [
        {
          label: 'Transfer deposit from',
          placeholder: 'Select an appointment',
          inputType: 'select',
          name: 'fromAppointmentId',
          options: (data?.from ?? []).map(appt => ({
            label: getAppointmentLabel(appt),
            value: appt.appointmentId,
          })),
          disabled: isLoading || isError,
        },
      ],
      [
        {
          label: 'Transfer deposit to',
          placeholder: 'Select an appointment',
          inputType: 'select',
          name: 'toAppointmentId',
          options: (data?.to ?? []).map(appt => ({
            label: getAppointmentLabel(appt, true),
            value: appt.appointmentId,
          })),
          disabled: true,
        },
      ],
    ],
    submitButtonText: 'Transfer',
  };

  return (
    <FormSmallModal
      open={isOpen}
      onClose={onClose}
      form={form}
      maxWidth={500}
      alert={(vals: FormikValues) => {
        const values = vals as AppointmentTransferDepositState;
        const isValid =
          isLoading ||
          !values.toAppointmentId ||
          data?.to.some(appt => appt.appointmentId === values.toAppointmentId);
        if (!isValid) {
          return {
            title: 'Invalid Appointment',
            subtitle:
              'The selected "Transfer deposit to" appointment is not available for transfer',
            type: 'error',
            animate: false,
            dismissible: false,
          };
        }
        return null;
      }}
    />
  );
}
